import MicPermissionGate from "./MicPermissionGate";
import type { TourConfig } from "./types";

const B = ({ children }: { children: string }) => <strong className="text-shadow-600">{children}</strong>;

/** Selector that only exists when document generation is NOT in progress — the toolbar is replaced by a stop button while generating. */
const DOC_TOOLBAR = '[data-tour="doc-toolbar"]';

export const welcomeTour: TourConfig = {
    id: "welcome",
    name: "Welcome Tour",
    description: "Record your first appointment and see HappyDoc in action",
    showInHelpMenu: true,
    steps: [
        // --- Intro ---
        {
            placement: "center",
            scoutState: "wave",
            title: "Welcome to HappyDoc!",
            content: "I'm Scout — let's make your first recording together so you can see HappyDoc in action.",
            route: "/",
        },

        // --- Mic permission gate ---
        // Auto-skips if mic is already granted; otherwise prompts the user before recording steps.
        {
            placement: "center",
            scoutState: "point",
            title: "Microphone Check",
            content: <MicPermissionGate />,
            route: "/",
        },

        // --- Record flow ---
        {
            target: '[data-tour="record-button"]',
            placement: "right",
            scoutState: "point",
            title: "Start Recording",
            content: "Tap the record button to begin. We'll do a quick demo together.",
            spotlightClicks: true,
            actionLabel: "Tap Record to start",
        },
        {
            target: "#recording-widget-stop-button",
            placement: "right",
            scoutState: "point",
            title: "Say Something",
            content:
                "Say: \"Hi, I'm Dr. Smith and today we're seeing Fluffy for a routine wellness exam.\" Then tap stop.",
            spotlightClicks: true,
            waitForTarget: true,
            actionLabel: "Tap Stop when you're done",
        },

        // --- Select document templates ---
        {
            target: '[data-tour="widget-document-select"]',
            placement: "left",
            scoutState: "point",
            title: "Choose Your Templates",
            content:
                "Select the document templates you'd like to generate. You can pick one or several — each checked template will produce a document from your recording.",
            spotlightClicks: true,
            waitForTarget: true,
            enableNextSelector: '[data-tour="recording-widget-generate"]:not(:disabled)',
        },

        // --- Generate from recording widget ---
        {
            target: '[data-tour="recording-widget-actions"]',
            placement: "top",
            scoutState: "point",
            title: "Generate Your Documents",
            content: "Now tap Generate to turn your recording into SOAP notes, discharge summaries, and more.",
            spotlightClicks: true,
            waitForTarget: true,
            actionLabel: "Tap Generate",
        },

        // --- Open the visit via recording widget's Visit button ---
        {
            target: '[data-tour="recording-widget-visit"]',
            placement: "top",
            scoutState: "point",
            title: "Open Your Visit",
            content: "Your recording has been saved. Tap Visit to see your documents.",
            spotlightClicks: true,
            waitForTarget: true,
            actionLabel: "Tap Visit to open it",
        },

        // --- Phase 1: Audio is processing (clip sidebar) ---
        {
            target: '[data-tour="clip-processing"]',
            placement: "left",
            scoutState: "point",
            title: "Processing Your Audio",
            content: "First, we process your recording to extract the conversation. This usually takes a few seconds.",
            waitForTarget: true,
            waitForTargetTimeout: 15000,
            advanceWhenSelector: '[data-tour="loading-generating"], [data-tour="doc-toolbar"]',
            onTimeoutGroup: "recovery",
            bailoutSelector: "[data-tour-asset-failed]",
        },

        // --- Phase 2: Document is generating (editor loading page) ---
        {
            target: '[data-tour="loading-generating"]',
            placement: "bottom",
            scoutState: "point",
            title: "Writing Your Documents",
            content: "Now HappyDoc is turning your conversation into medical records. Almost there!",
            waitForTarget: true,
            waitForTargetTimeout: 30000,
            advanceWhenSelector: '[data-tour="doc-toolbar"]',
            onTimeoutGroup: "recovery",
            bailoutSelector: "[data-tour-asset-failed]",
        },

        // --- Phase 3: Documents ready (toolbar appears) ---
        {
            target: DOC_TOOLBAR,
            placement: "bottom",
            scoutState: "celebrate",
            title: "Your Documents Are Ready!",
            content:
                "From here you can regenerate, copy, print, email, or give feedback on any document. These tools are always at the top of each document.",
            waitForTarget: true,
            waitForTargetTimeout: 10000,
            onTimeoutGroup: "recovery",
            bailoutSelector: "[data-tour-asset-failed]",
        },

        // --- Field editing ---
        {
            target: '[data-tooltip-id="field-actions-tooltip"]',
            placement: "left",
            scoutState: "point",
            title: "Edit Any Field",
            content:
                "Hover over any field in the document to see editing options. You can revert a field to its normal value, regenerate it, or type your own edits directly.",
            waitForTarget: true,
            disableAutoScroll: true,
        },

        // --- Add recording in patient view ---
        {
            target: "#in-line-start-recording",
            placement: "left",
            scoutState: "point",
            title: "Add More Recordings",
            content:
                "Need to record a follow-up or a different part of the visit? Tap here to add another recording to this visit.",
            waitForTarget: true,
        },

        // --- Upload files ---
        {
            target: "#in-line-upload-button",
            placement: "left",
            scoutState: "point",
            title: "Upload Files",
            content:
                "You can upload PDFs, JPG and PNG images, and audio files here. Uploaded files can be used alongside your recordings to generate documents.",
            waitForTarget: true,
        },

        // --- Assets library ---
        {
            target: '[data-tour="assets-library"]',
            placement: "left",
            scoutState: "point",
            title: "Your Library",
            content:
                "All your recordings and uploaded files live here. The checkboxes control which files are used when generating documents.",
            waitForTarget: true,
        },

        // --- Navigate back to visits ---
        {
            target: '[data-tour="recording-list"]',
            placement: "top",
            scoutState: "point",
            title: "Your Visits Live Here",
            content:
                "Every recording shows up as a visit. Once it's processed, your documents are ready to review and edit.",
            route: "/",
            waitForTarget: true,
        },

        // --- Doctor dropdown ---
        {
            target: '[data-tour="doctor-select"]',
            placement: "bottom",
            scoutState: "point",
            title: "Filter by Doctor",
            content:
                "Use this dropdown to filter visits by doctor. Handy when you have multiple doctors using HappyDoc.",
            route: "/",
            waitForTarget: true,
        },

        // --- Add visit button ---
        {
            target: '[data-tour="add-visit"]',
            placement: "bottom",
            scoutState: "point",
            title: "Add a Visit",
            content: (
                <>
                    Create a new visit manually here — useful when you want to upload files or start a recording for a
                    specific appointment. <B>Visits are added automatically when you connect a PIMS integration.</B>
                </>
            ),
            route: "/",
            waitForTarget: true,
        },

        // --- Settings ---
        {
            target: '[data-tour="settings-nav"]',
            placement: "bottom",
            scoutState: "point",
            title: "Make It Yours",
            content:
                "Head to Settings to customize your document templates, connect your PIMS for automatic writeback, and manage your team.",
            route: "/",
        },

        // --- Help ---
        {
            target: '[data-tour="help-menu"]',
            placement: "bottom",
            scoutState: "point",
            title: "Need Help?",
            content:
                "Tap here anytime to replay these tours or find the one you need. You can also reach our support team through the help center.",
            route: "/",
        },

        // --- Intercom support chat ---
        {
            target: ".intercom-launcher",
            placement: "left",
            scoutState: "celebrate",
            title: "We're Here to Help!",
            content:
                "If you ever have questions or run into any trouble, tap this chat bubble to reach our support team.",
            route: "/",
            waitForTarget: true,
            waitForTargetTimeout: 10000,
        },
    ],

    groups: {
        // Recovery: re-record when first attempt had no audio
        recovery: [
            {
                target: "#in-line-start-recording",
                placement: "left",
                scoutState: "point",
                title: "Let's Try Again",
                content:
                    "It looks like the recording didn't pick up any audio. Tap Record to try again — speak clearly into your microphone this time.",
                spotlightClicks: true,
                waitForTarget: true,
                actionLabel: "Tap Record",
            },
            {
                target: "#recording-widget-stop-button",
                placement: "right",
                scoutState: "point",
                title: "Record & Stop",
                content:
                    "Say: \"Hi, I'm Dr. Smith and today we're seeing Fluffy for a routine wellness exam.\" Then tap stop.",
                spotlightClicks: true,
                waitForTarget: true,
                actionLabel: "Tap Stop when you're done",
            },
            {
                target: '[data-tour="widget-document-select"]',
                placement: "left",
                scoutState: "point",
                title: "Choose Your Templates",
                content: "Select which document templates to generate, then continue.",
                spotlightClicks: true,
                waitForTarget: true,
                enableNextSelector: '[data-tour="recording-widget-generate"]:not(:disabled)',
            },
            {
                target: '[data-tour="recording-widget-actions"]',
                placement: "top",
                scoutState: "point",
                title: "Generate",
                content: "Tap Generate to create your documents.",
                spotlightClicks: true,
                waitForTarget: true,
                actionLabel: "Tap Generate",
            },
            {
                target: DOC_TOOLBAR,
                placement: "bottom",
                scoutState: "celebrate",
                title: "Your Documents Are Ready!",
                content: "Great — your documents are generated! Let me show you around.",
                waitForTarget: true,
                waitForTargetTimeout: 25000,
                onTimeoutGroup: "mic-help",
                bailoutSelector: "[data-tour-asset-failed]",
            },
        ],

        // Microphone troubleshooting — ends the tour
        "mic-help": [
            {
                placement: "center",
                scoutState: "wave",
                title: "Check Your Microphone",
                content:
                    "We weren't able to detect any audio. Please check that your microphone is connected and that HappyDoc has microphone permissions in your browser. Restart this tour from the Help menu once your microphone is working!",
                endsTour: true,
            },
        ],
    },
};
